import { useEffect, useMemo, useState } from "react";
import DatePickerZoom from "@/components/DatePickerZoom";
import { useLang } from "@/contexts/LangContext";

/**
 * Dual-mode birth-date input.
 *
 *  - "dob"  → user picks the exact date. Age is displayed read-only underneath.
 *  - "age"  → user enters years + months. `birth_date` is auto-computed
 *             (today − age) and the `approximate` flag is set to true.
 *
 * Props:
 *  - value              string ("yyyy-MM-dd" or "") — current birth_date
 *  - approximate        boolean — current birth_date_approximate flag
 *  - onChange({ birth_date, birth_date_approximate })
 *  - testIdPrefix       optional prefix for data-testids
 */
export default function BirthOrAgeInput({
  value,
  approximate,
  onChange,
  testIdPrefix = "birth",
}) {
  const { t } = useLang();
  // If we open on a patient that has approximate=true and a birth_date, start in "age" mode.
  const [mode, setMode] = useState(approximate && value ? "age" : "dob");
  const [years, setYears] = useState(0);
  const [months, setMonths] = useState(0);

  // Compute age (y, m) from a yyyy-MM-dd birth date
  const computedAge = useMemo(() => {
    if (!value) return null;
    const dob = new Date(value);
    if (Number.isNaN(dob.getTime())) return null;
    const now = new Date();
    let y = now.getFullYear() - dob.getFullYear();
    let m = now.getMonth() - dob.getMonth();
    if (now.getDate() < dob.getDate()) m -= 1;
    if (m < 0) {
      y -= 1;
      m += 12;
    }
    if (y < 0) return null;
    return { y, m };
  }, [value]);

  // Whenever we switch INTO age mode, seed the inputs from the current DOB.
  useEffect(() => {
    if (mode === "age" && computedAge) {
      setYears(computedAge.y);
      setMonths(computedAge.m);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  function toIsoDateString(d) {
    // yyyy-MM-dd in local time (avoid timezone shift)
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${yr}-${mo}-${da}`;
  }

  function handleAgeChange(nextYears, nextMonths) {
    const y = Math.max(0, parseInt(nextYears ?? 0, 10) || 0);
    const m = Math.max(0, Math.min(11, parseInt(nextMonths ?? 0, 10) || 0));
    setYears(y);
    setMonths(m);
    // Back-date from today
    const d = new Date();
    d.setDate(1); // avoid month-end overflow (Mar 31 → Feb doesn't exist)
    d.setMonth(d.getMonth() - m);
    d.setFullYear(d.getFullYear() - y);
    onChange({ birth_date: toIsoDateString(d), birth_date_approximate: true });
  }

  function handleDobChange(iso) {
    // User entered an exact date; the approximate flag turns off automatically.
    onChange({ birth_date: iso || "", birth_date_approximate: false });
  }

  return (
    <div>
      <div className="pf-label mb-1 flex items-center justify-between gap-2">
        <span>{t("birthDate")}</span>
        <div className="inline-flex rounded-md border border-pf-border overflow-hidden text-[11px] normal-case tracking-normal">
          <button
            type="button"
            data-testid={`${testIdPrefix}-mode-dob`}
            onClick={() => setMode("dob")}
            className={`px-2 py-0.5 transition-colors ${
              mode === "dob"
                ? "bg-pf-primary text-white"
                : "bg-white text-pf-text-secondary hover:bg-pf-bg"
            }`}
          >
            {t("modeDateOfBirth")}
          </button>
          <button
            type="button"
            data-testid={`${testIdPrefix}-mode-age`}
            onClick={() => setMode("age")}
            className={`px-2 py-0.5 transition-colors ${
              mode === "age"
                ? "bg-pf-primary text-white"
                : "bg-white text-pf-text-secondary hover:bg-pf-bg"
            }`}
          >
            {t("modeAge")}
          </button>
        </div>
      </div>

      {mode === "dob" ? (
        <>
          <DatePickerZoom
            testId={`${testIdPrefix}-date`}
            value={value}
            onChange={handleDobChange}
          />
          {computedAge && (
            <div className="mt-1 text-xs text-pf-text-secondary">
              {computedAge.y} {t("years")} {computedAge.m} {t("months")}
              {approximate && ` · ${t("approximateAge")}`}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <input
                data-testid={`${testIdPrefix}-years`}
                type="number"
                min={0}
                max={40}
                value={years}
                onChange={(e) => handleAgeChange(e.target.value, months)}
                className="pf-input"
              />
              <div className="text-[11px] text-pf-text-secondary mt-0.5 text-center">
                {t("years")}
              </div>
            </div>
            <div className="flex-1">
              <input
                data-testid={`${testIdPrefix}-months`}
                type="number"
                min={0}
                max={11}
                value={months}
                onChange={(e) => handleAgeChange(years, e.target.value)}
                className="pf-input"
              />
              <div className="text-[11px] text-pf-text-secondary mt-0.5 text-center">
                {t("months")}
              </div>
            </div>
          </div>
          <div className="mt-1 text-xs text-pf-text-secondary">{t("ageHint")}</div>
        </>
      )}
    </div>
  );
}
